import { ProductType } from '@/data/types/Product';
import { Box, ButtonBase, SxProps, Theme } from '@mui/material';
import { useMemo } from 'react';
import {
	getProductColor,
	getProductColorName,
	hasTwoColors,
} from 'presentation/utils/custom/productColor';
import { useVisibleColorsCount } from '@/utils/useVisibleColorsCount';
import { ColorOverflowIndicator } from '@/components/blocks/ColorOverflowIndicator';

interface Props {
	variants: ProductType[];
	selectedPartNumber: string;
	sx?: SxProps<Theme>;
	onSelectColor?: (item: ProductType) => void;
}

const DOT_SIZE = 33;
const DOT_GAP = 8;

export const ProductColor = ({
	variant,
	isSelected,
	onClick,
}: {
	variant: ProductType | string;
	isSelected: boolean;
	onClick: (e: any) => void;
}) => {
	const colors = getProductColor(variant);
	const activeDoubleColor = hasTwoColors(colors);
	const colorName = getProductColorName(variant);

	return (
		<ButtonBase
			title={colorName}
			aria-label={colorName}
			sx={{
				width: '33px',
				height: '33px',
				borderRadius: '50%',
				border: '0.6px solid',
				borderColor: isSelected ? '#000000' : 'transparent',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
			onClick={onClick}
		>
			<Box
				sx={{
					width: '27px',
					height: '27px',
					borderRadius: '50%',
					border: '1px solid #E0E0E0',
					overflow: 'hidden',
					position: 'relative',
					background: activeDoubleColor ? undefined : colors.color1,
				}}
			>
				{activeDoubleColor ? (
					<>
						<Box
							sx={{
								position: 'absolute',
								width: '100%',
								height: '100%',
								background: colors.color1,
								clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
							}}
						/>
						<Box
							sx={{
								position: 'absolute',
								width: '100%',
								height: '100%',
								background: colors.color2,
								clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
							}}
						/>
					</>
				) : null}
			</Box>
		</ButtonBase>
	);
};

export const ProductCardSelectColor = ({
	variants,
	selectedPartNumber,
	sx,
	onSelectColor,
}: Props) => {
	const { ref, visibleCount, overflowCount } = useVisibleColorsCount({
		itemSize: DOT_SIZE,
		gap: DOT_GAP,
		totalItems: variants.length,
	});

	// Presentational only: variants arrive already in the desired order (the base
	// color first is decided by whoever builds the card); this just renders the
	// first row that fits and highlights the selected one.
	const visibleVariants = useMemo(
		() => variants.slice(0, visibleCount),
		[variants, visibleCount]
	);

	const handleSelectColor = (variant: ProductType) => (e: any) => {
		e.preventDefault();
		e.stopPropagation();
		onSelectColor?.(variant);
	};

	return (
		<Box
			ref={ref}
			// `contain: inline-size` keeps this wrapper's width driven by the available
			// space and prevents the (nowrap) dots row from inflating flex ancestors.
			sx={{ width: '100%', minWidth: 0, overflow: 'hidden', contain: 'inline-size' }}
		>
			{variants.length > 0 ? (
				<Box sx={{ display: 'flex', gap: `${DOT_GAP}px`, flexWrap: 'nowrap', ...sx }}>
					{visibleVariants.map((variant) => {
						const isSelected = variant.partNumber === selectedPartNumber;

						return (
							<ProductColor
								key={variant.partNumber}
								variant={variant}
								isSelected={isSelected}
								onClick={handleSelectColor(variant)}
							/>
						);
					})}
					{overflowCount > 0 ? <ColorOverflowIndicator count={overflowCount} /> : null}
				</Box>
			) : null}
		</Box>
	);
};
